import { isPrerender } from "@faire/web/common/user-agent/isPrerender";
import { TypographyTags } from "@faire/web/slate/Typography";
import React from "react";
import styled, { css, RuleSet } from "styled-components";

interface IProps {
  children?: React.ReactNode | undefined;
  asTag?: TypographyTags; // The tag rendered to the dom
  isLoaded: boolean;
  dataTestId?: string;
  backgroundTreatment?: IFallbackTreatment;
  loadedTreatment?: IFallbackTreatment;
}

export type IFallbackTreatment = RuleSet<{ $isVisible?: boolean }>;

export const FallbackOpacityAnimation = (
  duration: string
): IFallbackTreatment => {
  return css<{ $isVisible?: boolean }>`
    opacity: ${(props) => (props.$isVisible ? 1 : 0)};
    transition: opacity ${duration} ease;
  `;
};

const INITIAL_VALUE = "initial";
const LOADING_BACKGROUND = "loading background";
const LOADED_CONTENT = "loaded content";
const FallbackHandlerContext = React.createContext(INITIAL_VALUE);

export const createFallbackHandler = <T,>(
  Comp: React.JSXElementConstructor<T>,
  styles?: {
    backgroundStyle?: IFallbackTreatment;
    loadedStyle?: IFallbackTreatment;
  }
) =>
  React.forwardRef<
    HTMLElement,
    IProps & T & { "data-test-id"?: string | undefined }
  >((props, ref) => {
    const {
      asTag,
      isLoaded,
      backgroundTreatment,
      loadedTreatment,
      children,
      ...rest
    } = props;
    const dataTestId = props["data-test-id"] ?? props.dataTestId;

    // Try to ignore IProps and only pass T to Wrapper, but somehow TS
    // complains about rest's type
    return (
      <Comp {...(rest as unknown as T)} as={asTag} ref={ref}>
        <FallbackHandlerContext.Consumer>
          {(contextValue) => {
            if (contextValue === INITIAL_VALUE) {
              // The main purpose of using a context here is to avoid duplicate nodes
              // in the DOM tree. For example, for a product tile, when we create
              // fallback at tile level and product name tile, what we want is:
              //            tileWrapper                                     tileWrapper
              //   tileFallBack   tileContent      NOT         tileFallBack             tileContent
              // nameFallBack       nameContent          nameFallBack nameContent  nameFallBack nameContent
              // which means each handler needs to know what content its in,
              //   - if there is no parent handler, the handler the parent handler (tileWrapper)
              //   - if there is a fallback handler above, the handler only needs to care about fallback (nameFallBack)
              //   - if there is a content handler above, the handler only needs to care about content (nameContent)

              return (
                <>
                  <FallbackHandlerContext.Provider value={LOADING_BACKGROUND}>
                    {/* we want to remove the fallback wrapper when the content is loaded, because having duplicate links on the page wastes our crawling budget */}
                    {isPrerender() && isLoaded ? null : (
                      <span style={{ position: "relative" }}>
                        <FallbackParentWrapper
                          data-test-id={`handler-fallback-container${
                            dataTestId ? `-${dataTestId}` : ""
                          }`}
                          $isVisible={!isLoaded}
                          aria-hidden={isLoaded}
                          $backgroundStyle={
                            backgroundTreatment ?? styles?.backgroundStyle
                          }
                        >
                          {children}
                        </FallbackParentWrapper>
                      </span>
                    )}
                  </FallbackHandlerContext.Provider>
                  <FallbackHandlerContext.Provider value={LOADED_CONTENT}>
                    <ContentWrapper
                      data-test-id={`handler-visible-container${
                        dataTestId ? `-${dataTestId}` : ""
                      }`}
                      $isVisible={isLoaded}
                      aria-hidden={!isLoaded}
                      $otherStyle={loadedTreatment ?? styles?.loadedStyle}
                    >
                      {children}
                    </ContentWrapper>
                  </FallbackHandlerContext.Provider>
                </>
              );
            } else if (contextValue === LOADING_BACKGROUND) {
              return (
                <FallbackChildWrapper
                  data-test-id={`handler-fallback-container${
                    dataTestId ? `-${dataTestId}` : ""
                  }`}
                  $isVisible={!isLoaded}
                  aria-hidden={isLoaded}
                  $backgroundStyle={
                    backgroundTreatment ?? styles?.backgroundStyle
                  }
                >
                  {children}
                </FallbackChildWrapper>
              );
            } else if (contextValue === LOADED_CONTENT) {
              return (
                <ContentWrapper
                  data-test-id={`handler-visible-container${
                    dataTestId ? `-${dataTestId}` : ""
                  }`}
                  $isVisible={isLoaded}
                  aria-hidden={!isLoaded}
                  $otherStyle={loadedTreatment ?? styles?.loadedStyle}
                >
                  {children}
                </ContentWrapper>
              );
            }
            return null;
          }}
        </FallbackHandlerContext.Consumer>
      </Comp>
    );
  });

const FallbackParentWrapper = styled.span<{
  $isVisible: boolean;
  $backgroundStyle?: IFallbackTreatment;
}>`
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  visibility: ${(props) => (props.$isVisible ? "visible" : "hidden")};
  ${(props) => props.$backgroundStyle};
`;

const ContentWrapper = styled.span<{
  $isVisible: boolean;
  $otherStyle?: IFallbackTreatment;
}>`
  visibility: ${(props) => (props.$isVisible ? "visible" : "hidden")};
  ${(props) => props.$otherStyle};
`;

const FallbackChildWrapper = styled.span<{
  $isVisible: boolean;
  $backgroundStyle?: IFallbackTreatment;
}>`
  visibility: ${(props) => (props.$isVisible ? "visible" : "hidden")};
  ${(props) => props.$backgroundStyle};
`;
